import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import ReactDOM from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faExclamationCircle,
  faInfoCircle,
  faTimesCircle,
  faTrash,
  faCircleNotch,
  faPen,
  faRepeat,
} from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import {
  getFirestore,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";

import { useParams } from "react-router-dom";
import { useEventId } from "../EventContext";
import { useAuth } from "@vionixasia/shared-components";

// Minimum interval between loops in milliseconds (default: 1 hour)
const DEFAULT_MIN_LOOP_INTERVAL_MS = 60 * 60 * 1000; // 60 minutes

export const InputBIBLoop = () => {
  const { realEventId: EVENT_ID } = useEventId();
  const { getIdToken } = useAuth();

  const authenticatedFetch = useCallback(async (url, options = {}) => {
    // Anyone may input (no account needed); a signed-in admin still sends their
    // token, which lets them overwrite a recorded time.
    const token = await getIdToken().catch(() => null);
    return fetch(url, {
      ...options,
      headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
  }, [getIdToken]);
  
  // Core state
  const [loopingCategories, setLoopingCategories] = useState([]); // All looping categories
  const [participants, setParticipants] = useState([]);
  const [bibNumber, setBibNumber] = useState("");
  const [message, setMessage] = useState("");
  const [inputCount, setInputCount] = useState({});
  const [pendingBibs, setPendingBibs] = useState([]);
  const [history, setHistory] = useState([]);
  const [eventStatus, setEventStatus] = useState("ongoing");
  const [eventName, setEventName] = useState("");
  const [clearHistoryWarning, setClearHistoryWarning] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  
  // Edit state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);

  const historyEndRef = useRef(null);
  const maxRetries = 3;
  
  // Storage key prefix for this specific page type
  const STORAGE_PREFIX = `loop_${EVENT_ID}_`;

  // Save to localStorage
  const saveToLocalStorage = useCallback((data) => {
    try {
      localStorage.setItem(`${STORAGE_PREFIX}bibNumber`, data.bibNumber || "");
      localStorage.setItem(`${STORAGE_PREFIX}message`, data.message || "");
      localStorage.setItem(`${STORAGE_PREFIX}inputCount`, JSON.stringify(data.inputCount || {}));
      localStorage.setItem(
        `${STORAGE_PREFIX}pendingBibs`,
        JSON.stringify(data.pendingBibs || [])
      );
      localStorage.setItem(
        `${STORAGE_PREFIX}history`,
        JSON.stringify(
          data.history?.map(({ icon, ...rest }) => ({
            ...rest,
            icon: typeof icon === "string" ? icon : "unknown",
          })) || []
        )
      );
      localStorage.setItem(
        `${STORAGE_PREFIX}participants`,
        JSON.stringify(data.participants || [])
      );
      localStorage.setItem(
        `${STORAGE_PREFIX}loopingCategories`,
        JSON.stringify(data.loopingCategories || [])
      );
    } catch (error) {
      console.error("[ERROR] Saving to localStorage:", error);
      setMessage(
        "Error: Local storage is full. Please sync data or clear history."
      );
    }
  }, [STORAGE_PREFIX]);

  // Index history for quick lookup
  const historyIndex = useMemo(() => {
    const index = {};
    history.forEach((entry) => {
      const key = `${entry.bib}_${entry.loopNumber}_${entry.eventId}`;
      index[key] = entry;
    });
    return index;
  }, [history]);

  // Load from localStorage
  useEffect(() => {
    try {
      const saved = {
        bibNumber: localStorage.getItem(`${STORAGE_PREFIX}bibNumber`) || "",
        message: localStorage.getItem(`${STORAGE_PREFIX}message`) || "",
        inputCount: JSON.parse(localStorage.getItem(`${STORAGE_PREFIX}inputCount`) || "{}"),
        pendingBibs: JSON.parse(localStorage.getItem(`${STORAGE_PREFIX}pendingBibs`) || "[]"),
        history: JSON.parse(localStorage.getItem(`${STORAGE_PREFIX}history`) || "[]").map(
          (entry) => ({
            ...entry,
            icon: typeof entry.icon === "string" ? entry.icon : "unknown",
          })
        ),
        participants: JSON.parse(localStorage.getItem(`${STORAGE_PREFIX}participants`) || "[]"),
        loopingCategories: JSON.parse(localStorage.getItem(`${STORAGE_PREFIX}loopingCategories`) || "[]"),
      };

      console.log(
        `[RAW LOAD] History: ${saved.history.length}, Pending: ${saved.pendingBibs.length}, Participants: ${saved.participants.length}`
      );

      // Force update: Change Pending → Successfully saved if pendingBibs is empty
      const updatedHistory = saved.history.map((entry) => {
        if (entry.status === "Syncing...") {
          console.log(`[RECOVERY] ${entry.bib}: Syncing... → Pending`);
          return { ...entry, status: "Pending", icon: "exclamation-circle" };
        }

        if (entry.status === "Pending" && saved.pendingBibs.length === 0) {
          console.log(
            `[FORCE GREEN] ${entry.bib}-Loop${entry.loopNumber}: Pending → Successfully saved!`
          );
          return {
            ...entry,
            status: "Successfully saved",
            icon: "check-circle",
          };
        }
        return entry;
      });

      setBibNumber(saved.bibNumber);
      setMessage(saved.message);
      setHistory(updatedHistory);
      setPendingBibs(saved.pendingBibs);
      setInputCount(saved.inputCount);
      setParticipants(saved.participants);
      if (saved.loopingCategories && saved.loopingCategories.length > 0) {
        setLoopingCategories(saved.loopingCategories);
        console.log(`[CACHE] Loaded ${saved.loopingCategories.length} looping categories from cache`);
      }

      localStorage.setItem(`${STORAGE_PREFIX}pendingBibs`, JSON.stringify(saved.pendingBibs));
      localStorage.setItem(`${STORAGE_PREFIX}history`, JSON.stringify(updatedHistory));

    } catch (error) {
      console.error("[ERROR] Loading localStorage:", error);
      setMessage("Error loading data. Please clear history.");
      setHistory([]);
      setPendingBibs([]);
      setInputCount({});
      setParticipants([]);
    }
  }, [STORAGE_PREFIX]);

  // Clean up duplicates in pendingBibs
  useEffect(() => {
    const uniquePendingBibs = [];
    const seen = new Set();
    for (const entry of pendingBibs) {
      const key = `${entry.bib}_${entry.loopNumber}_${entry.eventId}`;
      if (!seen.has(key)) {
        seen.add(key);
        uniquePendingBibs.push(entry);
      }
    }
    if (uniquePendingBibs.length !== pendingBibs.length) {
      setPendingBibs(uniquePendingBibs);
      saveToLocalStorage({
        bibNumber,
        message,
        inputCount,
        pendingBibs: uniquePendingBibs,
        history,
        participants,
        loopingCategories,
      });
    }
  }, [pendingBibs, bibNumber, message, inputCount, history, participants, saveToLocalStorage]);

  // Fetch event data and ALL looping category configurations
  useEffect(() => {
    const fetchEventData = async () => {
      try {
        const apiUrl = `${import.meta.env.VITE_API_BASE_URL}/api/timing/event_timing`;
        const response = await fetch(apiUrl);
        const data = await response.json();
        
        if (response.ok) {
          const eventData = data.find((event) => event.eventId === EVENT_ID);
          if (eventData) {
            setEventName(eventData.eventName || "");
            setEventStatus(eventData.status || "ongoing");
            
            // Collect ALL looping categories
            const loopingCats = (eventData.categories || [])
              .filter(cat => cat.isLooping)
              .map(cat => ({
                id: cat.id,
                name: cat.name,
                checkpoint: cat.checkpoints?.length > 0 ? {
                  id: cat.checkpoints[0].id || cat.checkpoints[0].locationId,
                  locationId: cat.checkpoints[0].locationId,
                  name: cat.checkpoints[0].name,
                } : null,
              }));
            
            if (loopingCats.length > 0) {
              setLoopingCategories(loopingCats);
              const categoryNames = loopingCats.map(c => c.name).join(", ");
              console.log(`[DEBUG] Loaded ${loopingCats.length} looping categories: ${categoryNames}`);
              // Save to localStorage for offline access
              localStorage.setItem(
                `${STORAGE_PREFIX}loopingCategories`,
                JSON.stringify(loopingCats)
              );
              setMessage("");
            } else {
              setMessage("No looping category found in this event.");
            }
            
            // Fetch participants
            const participantsResponse = await fetch(
              `${import.meta.env.VITE_API_BASE_URL}/api/timing/participants?eventId=${EVENT_ID}`
            );
            const participantsData = await participantsResponse.json();
            if (participantsResponse.ok && participantsData.participants) {
              setParticipants(participantsData.participants);
              localStorage.setItem(
                `${STORAGE_PREFIX}participants`,
                JSON.stringify(participantsData.participants)
              );
              console.log("[DEBUG] Participants fetched:", participantsData.participants.length);
            }
          } else {
            setMessage("Event not found.");
          }
        } else {
          setMessage("Failed to fetch event data from server.");
        }
      } catch (error) {
        console.error("[ERROR] Fetching event data:", error);
        setMessage("Error fetching event data. Using cache if available.");
      }
    };
    
    fetchEventData();
  }, [EVENT_ID, STORAGE_PREFIX]);

  // Scroll to end of history
  useEffect(() => {
    historyEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [history]);

  // Helper to safely get milliseconds from any timestamp format
  const getTimestampMs = (ts) => {
    if (!ts) return 0;
    if (typeof ts === 'number') return ts;
    if (typeof ts === 'string') return new Date(ts).getTime();
    if (ts.toDate && typeof ts.toDate === 'function') return ts.toDate().getTime();
    if (ts._seconds !== undefined || ts.seconds !== undefined) {
      return ((ts._seconds || ts.seconds) * 1000) + ((ts._nanoseconds || ts.nanoseconds || 0) / 1000000);
    }
    return new Date(ts).getTime();
  };

  // Get the last recorded loop for a BIB (using specified checkpoint)
  const getLastLoopInfo = useCallback(
    async (bib, checkpointLocationId) => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 2000); // 2s timeout from timeout

        const apiUrl = `${import.meta.env.VITE_API_BASE_URL}/api/timing/results?eventId=${EVENT_ID}&bibNumberInput=${bib}`;
        let response, data;
        
        try {
          response = await fetch(apiUrl, { signal: controller.signal });
        } finally {
          clearTimeout(timeoutId);
        }
        
        data = await response.json();
        
        if (response.ok && data.results) {
          const result = data.results.find((r) => String(r.bib) === String(bib));
          if (result?.checkpoints && checkpointLocationId) {
            const cpData = result.checkpoints[checkpointLocationId];
            if (cpData) {
              console.log(`[DEBUG] getLastLoopInfo BIB ${bib} RAW:`, cpData);
              // Find highest loop number
              let maxLoop = 0;
              let lastTime = null;
              
              Object.keys(cpData).forEach(key => {
                if (key.startsWith('loop') && cpData[key]?.time) {
                  const loopNum = parseInt(key.replace('loop', ''), 10);
                  if (loopNum > maxLoop) {
                    maxLoop = loopNum;
                    lastTime = cpData[key]?.time;
                  }
                }
              });
              
              // Also check for legacy single-time format
              if (cpData.time && maxLoop === 0) {
                maxLoop = 1;
                lastTime = cpData.time;
              }
              
              return {
                lastLoop: maxLoop,
                lastTime: getTimestampMs(lastTime),
                hasStartCrossing: !!cpData.startCrossing?.time,
              }; // Return MS
            }
          }
          // Result found but no checkpoint data = Loop 0
          return { lastLoop: 0, lastTime: null, hasStartCrossing: false };
        } else if (response.ok) {
           // No results found = Loop 0
           return { lastLoop: 0, lastTime: null, hasStartCrossing: false };
        } else {
           // Server error response
           throw new Error(`Server returned ${response.status}`);
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log("[OFFLINE] Server check timed out, using local data only.");
          return { lastLoop: 0, lastTime: null, hasStartCrossing: false };
        }
        console.error("[ERROR] Getting last loop info:", error);
        // CRITICAL: Throw error so sync knows lookup failed, instead of assuming Loop 0
        // BUT for input (handleSubmit), we might want to proceed with local data?
        // Let's rely on caller handling, but for input we need speed.
        // Actually, existing caller (handleSubmit) uses Promise.all. If this throws, Promise.all fails.
        // We should probably return 0 if it's a network error during input, but throw during sync?
        // For now, let's keep throwing for non-timeout errors to be safe for sync integrity.
        throw error;
      }
    },
    [EVENT_ID]
  );

  // Also check local history for pending entries
  const getLocalLastLoopInfo = useCallback(
    (bib) => {
      const bibHistory = history.filter(
        (entry) => String(entry.bib) === String(bib) && 
                   entry.eventId === EVENT_ID && 
                   !entry.status.includes("Error") &&
                   entry.status !== "Double input"
      ).sort((a, b) => (b.loopNumber || 0) - (a.loopNumber || 0));
      
      if (bibHistory.length > 0) {
        const latestEntry = bibHistory[0];
        return { 
          lastLoop: latestEntry.loopNumber || 0, 
          lastTime: getTimestampMs(latestEntry.time) // Return MS
        };
      }
      return { lastLoop: 0, lastTime: null };
    },
    [history, EVENT_ID]
  );

  const handleNumpadClick = useCallback(
    (value) => {
      if (eventStatus === "completed") {
        setMessage("Event completed. No inputs allowed.");
        return;
      }
      setClearHistoryWarning(false);
      setBibNumber((prev) => prev + value);
    },
    [eventStatus]
  );

  const handleDelete = useCallback(() => {
    if (eventStatus === "completed") {
      setMessage("Event completed. No inputs allowed.");
      return;
    }
    if (bibNumber) {
      setBibNumber((prev) => prev.slice(0, -1));
      setMessage("");
    } else {
      setMessage("No BIB number to delete.");
    }
  }, [bibNumber, eventStatus]);

  const handleClearHistoryClick = useCallback(() => {
    if (eventStatus === "completed") {
      setMessage("Event completed. No inputs allowed.");
      return;
    }
    if (clearHistoryWarning) {
      // Confirmed clear
      if (pendingBibs.length > 0) {
        setMessage("Cannot clear history while there are pending entries to sync.");
        setClearHistoryWarning(false);
        return;
      }
      
      if (window.confirm("Are you sure you want to clear history? This will remove local records.")) {
         saveToLocalStorage({
             bibNumber: "",
             message: "History cleared.",
             inputCount: {},
             pendingBibs: [],
             history: [],
             participants,
             loopingCategories,
         });
         setHistory([]);
         setPendingBibs([]);
         setInputCount({});
         setBibNumber("");
         setMessage("History cleared.");
      }
      setClearHistoryWarning(false);
    } else {
      // First click
      setClearHistoryWarning(true);
      setTimeout(() => setClearHistoryWarning(false), 3000); // Reset after 3s
    }
  }, [clearHistoryWarning, pendingBibs, participants, loopingCategories, saveToLocalStorage, eventStatus]);

  const handleDeleteEntry = useCallback(
    async (entryToDelete) => {
      if (eventStatus === "completed") {
        setMessage("Event completed. No updates allowed.");
        return;
      }
      if (!window.confirm(`Delete Loop ${entryToDelete.loopNumber} for BIB ${entryToDelete.bib}?`)) {
        return;
      }

      // If already synced, we need to delete from Firestore
      if (entryToDelete.status === "Successfully saved") {
        try {
          const db = getFirestore();
          const resultRef = doc(
            db,
            "event_timing",
            EVENT_ID,
            "results",
            String(entryToDelete.bib)
          );

          const docSnap = await getDoc(resultRef);
          if (docSnap.exists()) {
            const currentData = docSnap.data();
            const checkpointKey = entryToDelete.locationId;
            
            if (currentData.checkpoints?.[checkpointKey]) {
              const loopKey = `loop${entryToDelete.loopNumber}`;
              
              // Rewrite the nested checkpoint map as data. Location IDs may
              // legitimately contain '/', which cannot be used in dotted paths.
              const updatedCheckpoint = { ...currentData.checkpoints[checkpointKey] };
              delete updatedCheckpoint[loopKey];
              await updateDoc(resultRef, {
                checkpoints: {
                  ...(currentData.checkpoints || {}),
                  [checkpointKey]: updatedCheckpoint,
                },
                updatedAt: new Date().toISOString(),
              });
              
              console.log(`[DELETE SERVER] Deleted ${loopKey} for BIB ${entryToDelete.bib}`);
            }
          }
        } catch (error) {
          console.error("[DELETE ERROR] Failed to delete from server:", error);
          setMessage(`DELETE FAILED: ${error.message}`);
          return;
        }
      }

      // Filter out from history
      const newHistory = history.filter((e) => e !== entryToDelete);

      // Filter out from pendingBibs
      const newPendingBibs = pendingBibs.filter(
        (e) =>
          !(
            e.bib === entryToDelete.bib &&
            e.loopNumber === entryToDelete.loopNumber &&
            e.time === entryToDelete.time
          )
      );

      // Update inputCount
      const inputCountKey = entryToDelete.bib;
      const newInputCount = { ...inputCount };
      if (newInputCount[inputCountKey] > 0) {
        newInputCount[inputCountKey]--;
      }

      setHistory(newHistory);
      setPendingBibs(newPendingBibs);
      setInputCount(newInputCount);

      saveToLocalStorage({
        bibNumber,
        message: `Deleted Loop ${entryToDelete.loopNumber} for BIB ${entryToDelete.bib}.`,
        inputCount: newInputCount,
        pendingBibs: newPendingBibs,
        history: newHistory,
        participants,
        loopingCategories,
      });

      setMessage(`Loop ${entryToDelete.loopNumber} for BIB ${entryToDelete.bib} deleted.`);
    },
    [
      history,
      pendingBibs,
      inputCount,
      eventStatus,
      saveToLocalStorage,
      bibNumber,
      participants,
      EVENT_ID,
    ]
  );

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (eventStatus === "completed") {
        setMessage("Event completed. No inputs allowed.");
        return;
      }
      if (loopingCategories.length === 0 || !bibNumber) {
        setMessage("Please enter a BIB number.");
        return;
      }

      // Validate BIB exists in participants
      const participant = participants.find((p) => String(p.bib) === String(bibNumber));
      if (!participant) {
        setMessage(`BIB ${bibNumber} not found in participant list.`);
        setBibNumber("");
        return;
      }

      // Find the looping category for this participant
      const categoryConfig = loopingCategories.find(
        (cat) => cat.id === participant.categoryId
      );
      
      if (!categoryConfig) {
        setMessage(`BIB ${bibNumber} (${participant.categoryId}) is not in a looping category.`);
        setBibNumber("");
        return;
      }

      if (!categoryConfig.checkpoint) {
        setMessage(`No checkpoint configured for category ${categoryConfig.name}.`);
        setBibNumber("");
        return;
      }

      const { checkpoint } = categoryConfig;

      setMessage(`Checking loop status for ${categoryConfig.name}...`);
      
      // Get both server and local loop info
      let serverInfo = { lastLoop: 0, lastTime: null, hasStartCrossing: false };
      const localInfo = getLocalLastLoopInfo(bibNumber);
      
      try {
        serverInfo = await getLastLoopInfo(bibNumber, checkpoint.locationId);
      } catch (err) {
        console.log("[OFFLINE] Server check failed completely, ignoring server data.", err);
      }
      
      // Use the higher loop number between server and local
      // And select the corresponding time (or the latest one if equal)
      const lastLoop = Math.max(serverInfo.lastLoop, localInfo.lastLoop);
      let lastTime = null;

      if (serverInfo.lastLoop > localInfo.lastLoop) {
          lastTime = serverInfo.lastTime;
      } else if (localInfo.lastLoop > serverInfo.lastLoop) {
          lastTime = localInfo.lastTime;
      } else {
          // Same loop count: use the LATEST time to ensure SAFETY (stricter interval check)
          lastTime = Math.max(serverInfo.lastTime || 0, localInfo.lastTime || 0) || null;
      }

      const nextLoop = lastLoop + 1;
      
      console.log(`[DEBUG] BIB ${bibNumber} | Server: L${serverInfo.lastLoop} T${serverInfo.lastTime} | Local: L${localInfo.lastLoop} T${localInfo.lastTime} | Chosen: L${nextLoop-1} T${lastTime}`);

      const timestamp = new Date().toISOString();

      // Match the single endpoint and MYLAPS fallback contract: when runners
      // begin before the mat, their first crossing establishes the start line;
      // loop 1 is only completed on the next crossing.
      if (
        lastLoop === 0 &&
        !serverInfo.hasStartCrossing &&
        localInfo.lastLoop === 0
      ) {
        try {
          const response = await authenticatedFetch(`${import.meta.env.VITE_API_BASE_URL}/api/timing/record-time-loop`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              eventId: EVENT_ID,
              bib: bibNumber,
              checkpointId: checkpoint.locationId,
              timestamp,
            }),
          });
          const data = await response.json();
          if (!response.ok) throw new Error(data.message || `HTTP ${response.status}`);
          setMessage(
            data.isStartCrossing
              ? `${categoryConfig.name} start crossing for BIB ${bibNumber} recorded. Next crossing is Loop 1.`
              : (data.message || `Crossing for BIB ${bibNumber} was not counted.`),
          );
          setBibNumber("");
          return;
        } catch (error) {
          setMessage(`Start crossing for BIB ${bibNumber} failed: ${error.message}`);
          setBibNumber("");
          return;
        }
      }

      // Save locally
      const newInputCount = {
        ...inputCount,
        [bibNumber]: (inputCount[bibNumber] || 0) + 1,
      };
      const newHistoryEntry = {
        bib: bibNumber,
        locationId: checkpoint.locationId,
        location: checkpoint.name,
        eventId: EVENT_ID,
        loopNumber: nextLoop,
        categoryName: categoryConfig.name,
        time: timestamp,
        status: "Pending",
        icon: "exclamation-circle",
      };
      const newHistory = [...history, newHistoryEntry];
      const newPendingBibs = [
        ...pendingBibs,
        {
          bib: bibNumber,
          locationId: checkpoint.locationId,
          eventId: EVENT_ID,
          loopNumber: nextLoop,
          time: timestamp,
          status: "Pending",
          retries: 0,
        },
      ];

      setHistory(newHistory);
      setPendingBibs(newPendingBibs);
      setInputCount(newInputCount);
      setMessage(`${categoryConfig.name} Loop ${nextLoop} for BIB ${bibNumber} saved.`);
      setBibNumber("");

      saveToLocalStorage({
        bibNumber: "",
        message: `Loop ${nextLoop} for BIB ${bibNumber} saved locally.`,
        inputCount: newInputCount,
        pendingBibs: newPendingBibs,
        history: newHistory,
        participants,
        loopingCategories,
      });
    },
    [
      history,
      pendingBibs,
      inputCount,
      bibNumber,
      loopingCategories,
      participants,
      eventStatus,
      saveToLocalStorage,
      getLastLoopInfo,
      getLocalLastLoopInfo,
      EVENT_ID,
    ]
  );

  const handleMissedOpeningRecovery = useCallback(async () => {
    if (!bibNumber) {
      setMessage("Enter the affected BIB first.");
      return;
    }
    const participant = participants.find((p) => String(p.bib) === String(bibNumber));
    if (!participant) {
      setMessage(`BIB ${bibNumber} not found in participant list.`);
      return;
    }
    if (!window.confirm(`Confirm that BIB ${bibNumber}'s chip was NOT read at the opening, and its stored baseline is actually Loop 1?`)) return;
    try {
      const response = await authenticatedFetch(`${import.meta.env.VITE_API_BASE_URL}/api/timing/recover-missed-loop-opening`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ eventId: EVENT_ID, bib: String(bibNumber) }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || `HTTP ${response.status}`);
      setMessage(`Recovered BIB ${bibNumber}: ${data.beforeLoops} → ${data.afterLoops} completed loops. Raw MYLAPS evidence verified.`);
      setBibNumber("");
    } catch (error) {
      setMessage(`Recovery refused: ${error.message}`);
    }
  }, [bibNumber, participants, authenticatedFetch, EVENT_ID]);

  const handleSyncClick = useCallback(async () => {
    if (eventStatus === "completed") {
      setMessage("Event completed. No inputs allowed.");
      return;
    }
    if (!pendingBibs.length) {
      setMessage("No pending entries to sync.");
      return;
    }
    if (!navigator.onLine) {
      setMessage("Offline. Please connect to the internet to sync.");
      return;
    }

    setIsSyncing(true);
    let finalHistory = [...history];
    const BATCH_SIZE = 50;
    
    console.log(`[SYNC START] Processing ${pendingBibs.length} loop entries in batches of ${BATCH_SIZE}`);

    // Mark all pending as syncing
    finalHistory = finalHistory.map((entry) =>
      pendingBibs.some(
        (p) =>
          p.bib === entry.bib &&
          p.loopNumber === entry.loopNumber &&
          p.eventId === entry.eventId &&
          entry.status === "Pending"
      )
        ? { ...entry, status: "Syncing...", icon: "circle-notch" }
        : entry
    );
    setHistory(finalHistory);

    // Split into batches
    const batches = [];
    for (let i = 0; i < pendingBibs.length; i += BATCH_SIZE) {
      batches.push(pendingBibs.slice(i, i + BATCH_SIZE));
    }

    let totalSuccess = 0;
    let totalFailed = 0;
    const syncedEntries = new Set();

    // Process batches
    for (let batchIdx = 0; batchIdx < batches.length; batchIdx++) {
      const batch = batches[batchIdx];
      setMessage(`Syncing batch ${batchIdx + 1}/${batches.length} (${batch.length} entries)...`);

      try {
        // AUTO-CORRECT: Check server's lastLoop for each unique BIB and correct loopNumber if needed
        const uniqueBibs = [...new Set(batch.map(p => p.bib))];
        const serverLoopInfo = {};
        const invalidBibs = new Set(); // Bibs where we failed to verify server state
        
        // Fetch server state for each unique BIB
        for (const bib of uniqueBibs) {
          const entry = batch.find(p => p.bib === bib);
          if (entry && entry.locationId) {
            try {
              const info = await getLastLoopInfo(bib, entry.locationId);
              serverLoopInfo[bib] = info.lastLoop || 0;
              
              // Debug log only
              if (serverLoopInfo[bib] === 0) {
                 console.log(`[DEBUG] BIB ${bib} Server LastLoop: 0 - LocID: ${entry.locationId}`);
              }


              console.log(`[AUTO-CORRECT] BIB ${bib}: Server lastLoop = ${serverLoopInfo[bib]}`);
            } catch (err) {
              console.log(`[AUTO-CORRECT] BIB ${bib}: Failed to get server state, skipping sync for this BIB to prevent error.`, err);
              invalidBibs.add(bib);
            }
          }
        }
        
        // Filter out invalid BIBs from batch
        const validBatch = batch.filter(p => !invalidBibs.has(p.bib));
        
        if (validBatch.length === 0) {
           console.log(`[SYNC SKIP] All entries in batch ${batchIdx + 1} failed verification. Skipping.`);
           continue; 
        }

        // Correct loopNumbers based on server state
        // SMART AUTO-CORRECT: Only correct if there is a GAP (p.loopNumber > serverLastLoop + 1).
        // This allows EDITS (p.loopNumber <= serverLastLoop) to proceed without being incremented.
        const correctedBatch = validBatch.map(p => {
          const serverLastLoop = serverLoopInfo[p.bib] || 0;
          const correctionTarget = serverLastLoop + 1;
          
          if (p.loopNumber > correctionTarget) {
            console.log(`[AUTO-CORRECT] BIB ${p.bib}: Correcting Loop ${p.loopNumber} → Loop ${correctionTarget} (Gap Detected)`);
            
            // Update history to show correction
            finalHistory = finalHistory.map(entry =>
              entry.bib === p.bib &&
              entry.loopNumber === p.loopNumber &&
              entry.eventId === EVENT_ID &&
              entry.status === "Syncing..."
                ? { ...entry, loopNumber: correctionTarget, status: `Corrected to Loop ${correctionTarget}` }
                : entry
            );
            setHistory(finalHistory);
            
            // Update serverLoopInfo for next entry of same BIB
            serverLoopInfo[p.bib] = correctionTarget;
            
            return { ...p, loopNumber: correctionTarget };
          }
          // ELSE: If p.loopNumber <= correctionTarget, it is valid (Next Loop or Update/Edit). Keep it.
          return p;
        });
        
        // SPLIT into Inserts (New Loops) vs Updates (Existing Loops)
        // Backend Batch API ignores updates to existing loops to prevent accidental overwrites.
        // So for "Edits" (loopNumber <= serverLastLoop), we must use direct Firestore update.
        const inserts = [];
        const updates = [];
        
        correctedBatch.forEach(p => {
             const serverLastLoop = serverLoopInfo[p.bib] || 0;
             if (p.loopNumber <= serverLastLoop) {
                 updates.push(p);
             } else {
                 inserts.push(p);
             }
        });
        
        // 1. Process INSERTS via Batch API
        if (inserts.length > 0) {
            console.log(`[SYNC] Sending ${inserts.length} NEW loops via Batch API.`);
            const records = inserts.map((p) => ({
              bib: p.bib,
              checkpointId: p.locationId,
              loopNumber: p.loopNumber,
              timestamp: p.time,
            }));
    
            const apiUrl = `${import.meta.env.VITE_API_BASE_URL}/api/timing/record-time-loop-batch`;
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000); 
    
            try {
            const response = await authenticatedFetch(apiUrl, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    eventId: EVENT_ID,
                    records,
                  }),
                  signal: controller.signal,
                });
                clearTimeout(timeoutId);
        
                const data = await response.json();
        
                if (response.ok && data.results) {
                   handleSyncResults(data.results, inserts, "API");
                } else {
                     // Mark all inserts as failed
                     markBatchFailed(inserts, `API Error: ${response.status}`);
                }
            } catch (err) {
                markBatchFailed(inserts, `Network Error: ${err.message}`);
            }
        }
        
        // 2. Process UPDATES via Firestore (Direct Write)
        if (updates.length > 0) {
            console.log(`[SYNC] Processing ${updates.length} UPDATES via Firestore.`);
            const db = getFirestore();
            
            // Process updates sequentially or in small parallel groups to avoid overwhelming
            // We'll do Promise.all
            await Promise.all(updates.map(async (p) => {
                try {
                    const docRef = doc(db, "event_timing", EVENT_ID, "results", String(p.bib));
                    const docSnap = await getDoc(docRef);
                    const currentData = docSnap.exists() ? docSnap.data() : {};
                    const currentCheckpoints = currentData.checkpoints || {};
                    const currentCheckpoint = currentCheckpoints[p.locationId] || {};
                    const updatedCheckpoint = {
                      ...currentCheckpoint,
                      [`loop${p.loopNumber}`]: { time: p.time },
                    };
                    
                    // If this is the LATEST loop on server (or greater), update the main checkpoint time too
                    const serverLastLoop = serverLoopInfo[p.bib] || 0;
                    if (p.loopNumber >= serverLastLoop) {
                      updatedCheckpoint.time = p.time;
                    }
                    const updateData = {
                      checkpoints: {
                        ...currentCheckpoints,
                        [p.locationId]: updatedCheckpoint,
                      },
                    };
                    
                    await updateDoc(docRef, updateData);
                    
                    // Mark success manually
                    handleSyncResults([{ bib: p.bib, loopNumber: p.loopNumber, success: true }], [p], "Firestore");
                    
                } catch (err) {
                    console.error(`[SYNC UPDATE ERROR] BIB ${p.bib}:`, err);
                    handleSyncResults([{ bib: p.bib, loopNumber: p.loopNumber, success: false, message: err.message }], [p], "Firestore");
                }
            }));
        }

        // Helper to update local history state
        function handleSyncResults(results, sourceBatch, method) {
            const newHistoryState = [...finalHistory]; // We need to access latest or use functional state? 
            // Actually 'finalHistory' variable is local to this function scope but 'setHistory' updates state.
            // We should accumulate changes?
            // In layout like this, better to update 'finalHistory' object and setHistory at end?
            // But we have async splits.
            
            // We will use functional setHistory to be safe, OR just update the local 'finalHistory' variable if we assume single thread flow?
            // But we have 'await Promise.all' and 'await fetch'.
            // Better to trigger setHistory incrementally.
            
            results.forEach((result) => {
                 setHistory(currentHist => {
                     return currentHist.map(entry => {
                         // Find match
                        if (String(entry.bib) === String(result.bib) &&
                            (entry.loopNumber === result.loopNumber) && // Exact loop match
                            entry.eventId === EVENT_ID && 
                            entry.status.includes("Syncing") // Only update if still marking as syncing
                        ) {
                             if (result.success) {
                                 // Update syncedEntries set for pendingBibs cleanup
                                 syncedEntries.add(`${result.bib}_${result.loopNumber}`);
                                 return { ...entry, status: "Successfully saved", icon: "check-circle" };
                             } else {
                                 return { ...entry, status: `Error: ${result.message}`, icon: "times-circle" };
                             }
                        }
                        return entry;
                     });
                 });
                 
                 if (result.success) {
                     totalSuccess++;
                 } else {
                     totalFailed++;
                 }
            });
        }
        
        function markBatchFailed(batchItems, msg) {
             setHistory(currentHist => currentHist.map(entry => 
                 batchItems.some(b => b.bib === entry.bib && b.loopNumber === entry.loopNumber)
                 ? { ...entry, status: `Error: ${msg}`, icon: "times-circle" }
                 : entry
             ));
             totalFailed += batchItems.length;
        }

       /*
        // ORIGINAL SINGLE BATCH LOGIC REMOVED
        const records = correctedBatch.map((p) => ({ ... }));
        // ...
       */

      } catch (error) {
        console.error(`[BATCH ERROR] Batch ${batchIdx + 1} failed:`, error);
        // Mark batch as failed
        batch.forEach((p) => {
          finalHistory = finalHistory.map((entry) =>
            entry.bib === p.bib &&
            entry.loopNumber === p.loopNumber &&
            entry.eventId === EVENT_ID
              ? { ...entry, status: `Error: ${error.message}`, icon: "times-circle" }
              : entry
          );
          totalFailed++;
        });
        setHistory(finalHistory);
      }
    }

    // Final cleanup - remove synced entries from pending
    const finalPending = pendingBibs.filter(
      (p) => !syncedEntries.has(`${p.bib}_${p.loopNumber}`)
    );

    console.log(
      `[SYNC DONE] Success: ${totalSuccess}, Failed: ${totalFailed}, Remaining: ${finalPending.length}`
    );

    localStorage.setItem(`${STORAGE_PREFIX}pendingBibs`, JSON.stringify(finalPending));
    localStorage.setItem(`${STORAGE_PREFIX}history`, JSON.stringify(finalHistory));
    setPendingBibs(finalPending);

    const finalMessage =
      totalFailed === 0
        ? `Sync completed! ${totalSuccess} loop entries saved.`
        : `Sync completed: ${totalSuccess} success, ${totalFailed} errors.`;

    setMessage(finalMessage);
    setIsSyncing(false);
  }, [pendingBibs, history, eventStatus, EVENT_ID, STORAGE_PREFIX]);

  // Computed values
  const isInputDisabled = eventStatus === "completed" || loopingCategories.length === 0;
  const visibleHistory = history.slice(-50);

  const handleSaveEdit = async (newTime) => {
    if (!editingEntry) return;

    const userTimeZone =
      Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Makassar";

    // Parse the time string (HH:MM:SS) and create a proper Date
    const [hours, minutes, seconds] = newTime.split(":").map(Number);
    const originalDate = new Date(editingEntry.time);
    const newDate = new Date(originalDate);
    newDate.setHours(hours, minutes, seconds);
    const newTimestamp = newDate.toISOString();

    // Update history
    const updatedHistory = history.map((entry) =>
      entry.bib === editingEntry.bib &&
      entry.loopNumber === editingEntry.loopNumber &&
      entry.time === editingEntry.time
        ? { ...entry, time: newTimestamp, status: "Pending", icon: "exclamation-circle" }
        : entry
    );

    // Add to pending for sync
    const existingPending = pendingBibs.find(
      (p) =>
        p.bib === editingEntry.bib &&
        p.loopNumber === editingEntry.loopNumber
    );

    let updatedPending;
    if (existingPending) {
      updatedPending = pendingBibs.map((p) =>
        p.bib === editingEntry.bib && p.loopNumber === editingEntry.loopNumber
          ? { ...p, time: newTimestamp, forceUpdate: true }
          : p
      );
    } else {
      updatedPending = [
        ...pendingBibs,
        {
          bib: editingEntry.bib,
          locationId: editingEntry.locationId,
          eventId: EVENT_ID,
          loopNumber: editingEntry.loopNumber,
          time: newTimestamp,
          forceUpdate: true,
          retries: 0,
        },
      ];
    }

    setHistory(updatedHistory);
    setPendingBibs(updatedPending);
    setMessage(`Time updated for BIB ${editingEntry.bib} Loop ${editingEntry.loopNumber}.`);

    saveToLocalStorage({
      bibNumber,
      message,
      inputCount,
      pendingBibs: updatedPending,
      history: updatedHistory,
      participants,
      loopingCategories,
    });

    setIsEditModalOpen(false);
    setEditingEntry(null);
  };

  // Edit Modal Component
  const EditModal = ({ isOpen, onClose, onSave, initialTime, bib, loopNumber }) => {
    const [time, setTime] = useState(initialTime || "00:00:00");

    useEffect(() => {
      setTime(initialTime || "00:00:00");
    }, [initialTime]);

    if (!isOpen) return null;

    return ReactDOM.createPortal(
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-6 mx-4 w-full max-w-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Edit Time - BIB {bib} Loop {loopNumber}
          </h3>
          <input
            type="time"
            step="1"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-xl text-center text-lg mb-4"
          />
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={() => onSave(time)}
              className="flex-1 py-3 bg-gray-800 text-white rounded-xl hover:bg-gray-900"
            >
              Save
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  // History Row Component
  const HistoryRow = React.memo(({ entry, onDelete, onEdit }) => {
    const userTimeZone =
      Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Makassar";
    const [leftOpen, setLeftOpen] = useState(false);
    const [rightOpen, setRightOpen] = useState(false);

    return (
      <div className="relative overflow-hidden mb-1 border-b border-gray-100 last:border-0">
        {/* Delete Background */}
        <div
          className="absolute inset-y-0 right-0 bg-red-500 w-16 flex items-center justify-center cursor-pointer z-0"
          onClick={() => onDelete(entry)}
        >
          <FontAwesomeIcon icon={faTrash} className="text-white w-4 h-4" />
        </div>

        {/* Edit Background */}
        <div
          className="absolute inset-y-0 left-0 bg-blue-500 w-16 flex items-center justify-center cursor-pointer z-0"
          onClick={() => onEdit(entry)}
        >
          <FontAwesomeIcon icon={faPen} className="text-white w-4 h-4" />
        </div>

        {/* Swipeable Content */}
        <motion.div
          className="relative grid grid-cols-4 gap-4 py-2 text-sm items-center bg-white z-20 w-full"
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.1}
          animate={{ x: leftOpen ? -64 : rightOpen ? 64 : 0 }}
          onDragEnd={(e, { offset }) => {
            if (offset.x < -40) {
              setLeftOpen(true);
              setRightOpen(false);
            } else if (offset.x > 40) {
              setRightOpen(true);
              setLeftOpen(false);
            } else {
              setLeftOpen(false);
              setRightOpen(false);
            }
          }}
          onClick={() => {
            setLeftOpen(false);
            setRightOpen(false);
          }}
          whileTap={{ cursor: "grabbing" }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          <div>{entry.bib}</div>
          <div>
            {new Date(entry.time).toLocaleTimeString("en-GB", {
              timeZone: userTimeZone,
              hour12: false,
            })}
          </div>
          <div>
            Loop {entry.loopNumber || "?"}
          </div>
          <div className="flex justify-center items-center gap-2">
            {entry.status === "Pending" && (
              <FontAwesomeIcon
                icon={faExclamationCircle}
                className="text-yellow-500"
              />
            )}
            {entry.status === "Successfully saved" && (
              <FontAwesomeIcon
                icon={faCheckCircle}
                className="text-green-500"
              />
            )}
            {entry.status.includes("Error") && (
              <FontAwesomeIcon icon={faTimesCircle} className="text-red-500" />
            )}
            {entry.status === "Double input" && (
              <FontAwesomeIcon icon={faInfoCircle} className="text-blue-500" />
            )}
            {entry.status === "Syncing..." && (
              <FontAwesomeIcon
                icon={faCircleNotch}
                className="text-blue-500 animate-spin"
              />
            )}
          </div>
        </motion.div>
      </div>
    );
  });

  return (
    <div className="bg-white">
      <div className="text-center h-screen flex flex-col justify-between py-10 max-w-screen-sm mx-auto">
        {/* History Table - same structure as InputBIB */}
        <div
          className="mx-4 bg-white border border-gray-300 rounded-2xl relative"
          style={{ flex: 1, overflowY: "auto", scrollbarWidth: "none" }}
        >
          <div className="grid grid-cols-4 gap-4 sticky top-0 bg-white text-gray-700 pt-4 pb-1 z-30 text-sm font-semibold">
            <div>BIB</div>
            <div>Time</div>
            <div>Loop</div>
            <div>Status</div>
          </div>
          <div className="overflow-y-auto max-h-[300px] relative">
            {visibleHistory.length === 0 ? (
              <div className="text-center text-sm py-8 text-gray-500">
                No history available
              </div>
            ) : (
              visibleHistory.map((entry, index) => (
                <HistoryRow
                  key={`${entry.bib}_${entry.loopNumber}_${entry.time}`}
                  entry={entry}
                  onDelete={handleDeleteEntry}
                  onEdit={(entry) => {
                    setEditingEntry(entry);
                    setIsEditModalOpen(true);
                  }}
                />
              ))
            )}
            <div ref={historyEndRef} />
          </div>
        </div>

        {/* BIB Input */}
        <input
          type="text"
          value={bibNumber}
          readOnly
          placeholder="Enter BIB number"
          className={`mx-4 text-2xl text-center py-4 mt-4 px-4 border border-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-400 rounded-2xl bg-white ${
            isInputDisabled
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : ""
          }`}
          disabled={isInputDisabled}
        />

        {message && (
          <p className="text-gray-600 mt-4 font-normal text-sm mx-4">{message}</p>
        )}

        {/* Numpad */}
        <form onSubmit={handleSubmit} className="w-full py-5">
          <div className="grid grid-cols-3 gap-2 px-4">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
              <button
                key={num}
                type="button"
                onClick={() => handleNumpadClick(num)}
                className={`py-4 text-lg border border-gray-300 rounded-2xl cursor-pointer transform transition-all duration-200 ease-in-out active:scale-108 active:shadow-md ${
                  isInputDisabled
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white hover:bg-gray-50 active:bg-gray-100"
                }`}
                disabled={isInputDisabled}
              >
                {num}
              </button>
            ))}
            <button
              type="button"
              onClick={handleSyncClick}
              disabled={isSyncing || isInputDisabled || !pendingBibs.length}
              className={`py-4 text-lg border border-gray-300 rounded-2xl cursor-pointer transform transition-all duration-200 ease-in-out active:scale-108 active:shadow-md ${
                isSyncing || isInputDisabled || !pendingBibs.length
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50 active:bg-gray-100"
              }`}
            >
              {isSyncing ? "Sync..." : "Sync"}
            </button>
            <button
              type="button"
              onClick={() => handleNumpadClick(0)}
              className={`py-4 text-lg border border-gray-300 rounded-2xl cursor-pointer transform transition-all duration-200 ease-in-out active:scale-108 active:shadow-md ${
                isInputDisabled
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50 active:bg-gray-100"
              }`}
              disabled={isInputDisabled}
            >
              0
            </button>
            <button
              type="button"
              onClick={handleDelete}
              className={`py-4 text-lg border border-gray-300 rounded-2xl cursor-pointer transform transition-all duration-200 ease-in-out active:scale-108 active:shadow-md ${
                isInputDisabled
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50 active:bg-gray-100"
              }`}
              disabled={isInputDisabled}
            >
              Delete
            </button>
            <button
              type="button"
              onClick={handleClearHistoryClick}
              className={`py-4 text-lg border border-gray-300 rounded-2xl cursor-pointer transform transition-all duration-200 ease-in-out active:scale-108 active:shadow-md ${
                isInputDisabled
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-red-500 text-white hover:bg-red-600 active:bg-red-700"
              }`}
              disabled={isInputDisabled}
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
            <button
              type="submit"
              className={`col-span-2 py-2 text-lg font-semibold border-none rounded-2xl cursor-pointer transform transition-all duration-200 ease-in-out active:scale-108 active:shadow-md ${
                isInputDisabled
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-gray-800 text-white hover:bg-gray-900 active:bg-black"
              }`}
              disabled={isInputDisabled}
            >
              Submit
            </button>
            <button
              type="button"
              onClick={handleMissedOpeningRecovery}
              className="col-span-3 py-3 text-sm font-semibold border border-amber-300 rounded-2xl bg-amber-50 text-amber-900 hover:bg-amber-100"
              disabled={isInputDisabled || !bibNumber}
            >
              Recover Missed Opening (requires raw MYLAPS evidence)
            </button>
          </div>
        </form>
      </div>

      <EditModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingEntry(null);
        }}
        onSave={handleSaveEdit}
        initialTime={
          editingEntry
            ? new Date(editingEntry.time).toLocaleTimeString("en-GB", {
                hour12: false,
              })
            : ""
        }
        bib={editingEntry?.bib}
        loopNumber={editingEntry?.loopNumber}
      />
    </div>
  );
};
